'use client';

import { useState } from 'react';

import { UserApi } from '@ps/api/user';
import { withClientAPI } from '@ps/functions/utils/api';
import { getRecaptchaToken } from '@ps/functions/utils/recaptcha';
import { RecaptchaAction } from '@ps/models/lib/shared';
import type { CreatorInfo } from '@ps/types/creator';
import { Checkbox } from '@ps/ui/atoms/checkbox';
import { Field, FieldError } from '@ps/ui/atoms/field';
import { Input } from '@ps/ui/atoms/input';
import { Label } from '@ps/ui/atoms/label';
import { Send } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { AgentInfo } from './agent-info';

// ─── Types ──────────────────────────────────────────────────────────────

interface ContactFormProps {
  storeProfile: CreatorInfo;
  creatorId?: string;
  listingId?: string;
  listingSlug?: string;
  listingName?: string;
  /** When true, the form is non-interactive (edit/preview mode). */
  disabled?: boolean;
  /**
   * Compact mode for chat-bubble embeds. Hides the built-in AgentInfo
   * header (caller renders its own), tightens sizes/spacing, and uses
   * light-only styling so the form always renders light inside dark
   * pages (echo bubbles are wrapped in `.force-light`).
   */
  compact?: boolean;
  /** Called after a successful submission. */
  onSubmitted?: () => void;
}

interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  sharePhoneConsent: boolean;
  offerConsent: boolean;
}

// ─── Validation ─────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s\-().]{7,20}$/;

// ─── Contact Form ───────────────────────────────────────────────────────

export function ContactForm({
  storeProfile,
  creatorId,
  listingId,
  listingSlug,
  listingName,
  disabled,
  compact = false,
  onSubmitted,
}: ContactFormProps) {
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      sharePhoneConsent: true,
      offerConsent: true,
    },
  });

  const sharePhoneConsent = watch('sharePhoneConsent');
  const offerConsent = watch('offerConsent');
  const creatorName = storeProfile.name || 'the agent';

  async function onSubmit(data: ContactFormValues) {
    if (disabled) {
      toast.success('Message sent! The agent will be in touch soon.');
      return;
    }
    if (!creatorId || !listingId) return;

    try {
      const recaptchaToken = await getRecaptchaToken(
        RecaptchaAction.LeadContactCreation,
      );

      const api = withClientAPI(UserApi);
      await api.userApiControllerCreateLeadsContact({
        CreateLeadsContactRequest: {
          creatorId,
          sourceType: 'REAL_ESTATE_LISTING',
          sourceId: listingId,
          source: 'listing-page',
          name: data.name || undefined,
          email: data.email || undefined,
          phoneNumber: data.phone || undefined,
          sharePhoneConsent: data.sharePhoneConsent,
          offerConsent: data.offerConsent,
          recaptchaToken,
          context: {
            listingId,
            listingSlug,
            listingName,
            listingUrl: window.location.href,
            userAgent: navigator.userAgent,
          },
        },
      });

      setSubmitted(true);
      toast.success('Message sent! The agent will be in touch soon.');
      onSubmitted?.();
    } catch {
      toast.error('Failed to send message. Please try again.');
    }
  }

  // ── Styling ──────────────────────────────────────────────────────────
  // Compact mode (echo-bot bubble): tighter sizing, light-only classes.
  // `force-light:` variants are required on any class that has a `dark:`
  // counterpart (in the Input atom or here), because inside a dark page
  // the bubble is wrapped in `.force-light` — `:is()` specificity beats
  // `:where(.dark)` so only `force-light:` overrides reliably win.
  const formSpacing = compact ? 'space-y-1.5' : 'space-y-4';
  const inputClass = compact
    ? 'force-light:border-neutral-200 force-light:bg-white force-light:text-gray-900 force-light:placeholder:text-neutral-400 h-6 rounded border-neutral-200 bg-white px-1.5 py-0 text-[10px] md:text-[10px] font-light leading-none tracking-[0.2px] text-gray-900 shadow-none placeholder:text-[10px] placeholder:text-neutral-400'
    : 'h-[42px] rounded-[10px] border-gray-200 text-sm font-light tracking-[0.2px] dark:border-gray-600 dark:bg-gray-700 dark:text-white';
  const labelClass = compact
    ? 'force-light:text-gray-700 text-[10px] md:text-[10px] leading-[1.2] font-light tracking-[0.2px] text-gray-700'
    : 'text-sm leading-[1.2] font-light tracking-[0.2px] text-gray-800 dark:text-gray-300';
  const submitButtonClass = compact
    ? 'bg-mint-green-300 flex h-7 w-full items-center justify-center gap-1 rounded-md px-2 py-0 text-[11px] md:text-[11px] font-medium leading-none tracking-[0.2px] text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
    : 'bg-mint-green-300 flex h-[46px] w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium tracking-[0.2px] text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
  const sendIconClass = compact ? 'h-3 w-3' : 'h-[18px] w-[18px]';

  if (submitted) {
    return (
      <div className={formSpacing}>
        {!compact && <AgentInfo storeProfile={storeProfile} />}
        <div
          className={
            compact
              ? 'flex flex-col items-center gap-1.5 py-3 text-center'
              : 'flex flex-col items-center gap-2 py-6 text-center'
          }
        >
          <div
            className={
              compact
                ? 'bg-mint-green-300 flex h-8 w-8 items-center justify-center rounded-full'
                : 'bg-mint-green-300 flex h-12 w-12 items-center justify-center rounded-full'
            }
          >
            <Send
              className={
                compact ? 'h-3.5 w-3.5 text-black' : 'h-5 w-5 text-black'
              }
            />
          </div>
          <p
            className={
              compact
                ? 'text-xs font-medium tracking-[0.2px] text-gray-900'
                : 'text-base font-medium tracking-[0.2px] text-gray-900 dark:text-white'
            }
          >
            Thank you!
          </p>
          <p
            className={
              compact
                ? 'text-[10px] font-light tracking-[0.2px] text-gray-500'
                : 'text-sm font-light tracking-[0.2px] text-gray-500'
            }
          >
            {creatorName} will reach out to you soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={
        disabled
          ? event => {
              event.preventDefault();
              toast.success('Message sent! The agent will be in touch soon.');
            }
          : handleSubmit(onSubmit)
      }
      className={formSpacing}
    >
      {!compact && <AgentInfo storeProfile={storeProfile} />}

      {/* Form fields */}
      <Field data-invalid={errors.name ? true : undefined}>
        <Input
          placeholder="Your Name"
          {...register('name', { required: 'Name is required' })}
          readOnly={disabled}
          aria-invalid={errors.name ? true : undefined}
          className={inputClass}
        />
        {errors.name && <FieldError>{errors.name.message}</FieldError>}
      </Field>
      <Field data-invalid={errors.email ? true : undefined}>
        <Input
          type="email"
          placeholder="Your Email"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: EMAIL_RE,
              message: 'Please enter a valid email address',
            },
          })}
          readOnly={disabled}
          aria-invalid={errors.email ? true : undefined}
          className={inputClass}
        />
        {errors.email && <FieldError>{errors.email.message}</FieldError>}
      </Field>
      <Field data-invalid={errors.phone ? true : undefined}>
        <Input
          type="tel"
          placeholder="Your Phone"
          {...register('phone', {
            required: 'Phone is required',
            pattern: {
              value: PHONE_RE,
              message: 'Please enter a valid phone number',
            },
          })}
          readOnly={disabled}
          aria-invalid={errors.phone ? true : undefined}
          className={inputClass}
        />
        {errors.phone && <FieldError>{errors.phone.message}</FieldError>}
      </Field>

      {/* Consent checkboxes */}
      <div className="relative flex items-start gap-2">
        <Checkbox
          id="share-phone-consent"
          checked={sharePhoneConsent}
          onCheckedChange={
            disabled
              ? undefined
              : checked => setValue('sharePhoneConsent', checked === true)
          }
          className={`data-[state=checked]:bg-mint-green-300 data-[state=checked]:border-mint-green-300 mt-0.5 data-[state=checked]:text-black ${compact ? 'force-light:border-neutral-300 force-light:bg-white size-3 border-neutral-300 bg-white shadow-none [&_svg]:size-2.5' : ''}`}
        />
        <Label htmlFor="share-phone-consent" className={labelClass}>
          I consent to share my phone number with {creatorName}
        </Label>
      </div>

      <div className="relative flex items-start gap-2">
        <Checkbox
          id="offer-consent"
          checked={offerConsent}
          onCheckedChange={
            disabled
              ? undefined
              : checked => setValue('offerConsent', checked === true)
          }
          className={`data-[state=checked]:bg-mint-green-300 data-[state=checked]:border-mint-green-300 mt-0.5 data-[state=checked]:text-black ${compact ? 'force-light:border-neutral-300 force-light:bg-white size-3 border-neutral-300 bg-white shadow-none [&_svg]:size-2.5' : ''}`}
        />
        <Label htmlFor="offer-consent" className={labelClass}>
          I agree to receive marketing emails from {creatorName}
        </Label>
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className={submitButtonClass}
      >
        <Send className={sendIconClass} />
        {isSubmitting ? 'Sending...' : 'Connect with me'}
      </button>
    </form>
  );
}
